// The Secrets and Platform keys pages: the lists, their side cards and the usage log.
import type { ReactNode } from "react";

import { plural, rich } from "../../../helpers";

const p = plural("en");

export default {
  page: {
    description: "Used by the broker on the agent's behalf; never sent to a worker unless you say so. Changes are signed by this device.",
    add: "Add secret",
    checkingKeys: "Checking this browser's keys…",
    orgsError: "Could not load the organisations",
    noOrgs: "No organisations",
    noOrgsBody: "Secrets belong to an organisation; a platform admin creates the first one.",
    deleted: (name: string) => `Deleted ${name}`,
    saved: (name: string) => `Saved ${name}`,
    replaced: (name: string) => `Replaced the value of ${name}`,
    updated: (name: string) => `Saved the changes to ${name}`,
    unlinkedLabel: "This browser is not linked",
    unlinkedTitle: "Link this browser to add or change secrets",
    unlinkedBody: (link: ReactNode) =>
      rich(
        "Every change to a secret is a request signed with a device key, so a stolen session or a script on the network cannot swap one. This browser has no device key yet. ",
        link,
        ", then come back. You can still see the list and each secret's usage.",
      ),
    unlinkedLink: "Link it on the Devices page",
  },
  table: {
    label: "Secrets",
    name: "Name",
    value: "Value",
    writeOnly: "Write-only",
    usableFor: "Usable for",
    kept: "Kept",
    lastUsed: "Last used",
    never: "never",
    agentCanRead: "Agent can read",
    holder: { server: "Server, sealed", devices: "My devices" },
    usage: "Usage",
    usageTip: "Usage log",
    edit: "Edit",
    editLabel: (name: string) => `Edit ${name}`,
    editUnlinked: "Link this browser on the Devices page to edit secrets",
    replace: "Replace value",
    deleteLabel: (name: string) => `Delete ${name}`,
    loadError: "Could not load the secrets",
    empty: "No secrets",
    emptyBody: "Add a token, API key or database login. Runs use it through the broker; the agent never sees it unless you allow that.",
  },
  aside: {
    protection: "How a secret is protected",
    keys: {
      data: { title: "A data key per secret", text: "Encrypts the value (AES-256-GCM). Stored only wrapped." },
      master: { title: "The master key", text: "Wraps every data key. One file only ocs-infrad can read." },
      sealing: { title: "The sealing key", text: "Your browser encrypts the value to it, so the API never sees it." },
    },
    footnote: "The database alone is useless: opening a secret needs the database and the master key file.",
    rotation: "Rotation",
    rotated: (day: string) => `rotated ${day}`,
    added: (day: string) => `added ${day}`,
  },
  usage: {
    label: (name: string) => `Usage of ${name}`,
    title: (name: ReactNode) => rich("Usage: ", name),
    description: "Every create, replacement and open of this secret",
    events: (n: number) => p(n, { one: "# event", other: "# events" }),
    notUsed: "Not used yet",
    notUsedBody: "Every create, replacement and open is listed here.",
    run: (id: string) => `run ${id}`,
  },
  platform: {
    add: "Add key",
    unlinked: (link: ReactNode) =>
      rich("Adding or deleting a platform key is a request signed with a device key. ", link, " first; you can still see the list."),
    linkBrowser: "Link this browser",
    kind: "Kind",
    loadError: "Could not load the platform keys",
    empty: "No platform keys",
    emptyBody: "Add a Claude subscription token or an Anthropic API key. Workflows whose model key falls back to the platform use it.",
    tableLabel: "Platform keys",
    deleteQuestion: (name: string) => `Delete ${name}?`,
    deleteBody: "Task groups that fall back to the platform key fail at their next model call until another key is added. Signed by this browser.",
    drawerTitle: "Add a platform key",
    drawerSubtitle: "Used when a user binds no model key of their own",
    save: "Save key",
  },
};
